import { useSubscription } from "@apollo/client";
import { isAuthenticated } from "@src/auth/auth-utils";
import { graphql } from "@src/gql/gql";
import { MentionInboxEventType } from "@src/gql/graphql";
import { useQueryClient } from "@tanstack/react-query";
import { mentionIdsEqual } from "../normalize-mention-id";
import { mentionsQueryKeys } from "../queries/query-keys";
import {
	type MentionInboxPayload,
	mentionInboxUpdatePayloadSchema,
} from "../schema";

export const onMentionInboxUpdatedSubscription = graphql(/* GraphQL */ `
	subscription OnMentionInboxUpdated {
		onMentionInboxUpdated {
			eventType
			mentionId
			unreadCount
			mention {
				id
				idempotencyId
				authorId
				authorName
				authorAvatarUrl
				sourceId
				anchorId
				snippet
				deepLink
				createdAt
				isRead
				readAt
			}
		}
	}
`);

function getInboxUnreadOnly(queryKey: readonly unknown[]): boolean {
	const params = queryKey[2];
	if (
		params &&
		typeof params === "object" &&
		"unreadOnly" in params &&
		typeof params.unreadOnly === "boolean"
	) {
		return params.unreadOnly;
	}
	return false;
}

export function useMentionInboxSubscription() {
	const queryClient = useQueryClient();

	useSubscription(onMentionInboxUpdatedSubscription, {
		skip: !isAuthenticated(),
		onData: (data) => {
			const raw = data.data?.data?.onMentionInboxUpdated;
			if (!raw) return;

			const parsed = mentionInboxUpdatePayloadSchema.safeParse(raw);
			if (!parsed.success) return;

			const payload = parsed.data;

			queryClient.setQueryData(
				mentionsQueryKeys.unreadCount(),
				payload.unreadCount,
			);

			const inboxQueries = queryClient.getQueriesData<MentionInboxPayload>({
				queryKey: mentionsQueryKeys.inboxRoot(),
			});

			for (const [queryKey, oldData] of inboxQueries) {
				if (!oldData) continue;

				const unreadOnly = getInboxUnreadOnly(queryKey);
				let next: MentionInboxPayload | undefined;

				if (payload.mention) {
					const newItem = payload.mention;
					const exists = oldData.items.some((i) =>
						mentionIdsEqual(i.id, newItem.id),
					);
					if (exists) {
						continue;
					}
					if (unreadOnly && newItem.isRead) {
						continue;
					}
					next = {
						...oldData,
						items: [newItem, ...oldData.items],
					};
				} else if (
					payload.eventType === MentionInboxEventType.MentionMarkedRead &&
					payload.mentionId
				) {
					const markedId = payload.mentionId;
					if (unreadOnly) {
						next = {
							...oldData,
							items: oldData.items.filter(
								(item) => !mentionIdsEqual(item.id, markedId),
							),
						};
					} else {
						next = {
							...oldData,
							items: oldData.items.map((item) =>
								mentionIdsEqual(item.id, markedId)
									? { ...item, isRead: true }
									: item,
							),
						};
					}
				} else if (payload.eventType === MentionInboxEventType.AllMarkedRead) {
					if (unreadOnly) {
						next = {
							...oldData,
							items: [],
						};
					} else {
						next = {
							...oldData,
							items: oldData.items.map((item) => ({
								...item,
								isRead: true,
							})),
						};
					}
				}

				if (next) {
					queryClient.setQueryData(queryKey, next);
				}
			}
		},
		onError: (error) => {
			console.warn("Mention inbox subscription error:", error);
		},
	});
}
